import { Button, FormLabel, InlineNotification, SkeletonText, Stack, TextInput, Tile } from '@carbon/react';
import { asProblem, hasPermission, PageHeader, problemMessage, useAdmin } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';

const brandingKey = ['ho', 'tenant-branding'] as const;

type Fields = { productName: string; copyright: string; poweredByName: string; poweredByUrl: string };

/**
 * The platform note every app's sign-in screen and footer show (ADR 0106): the product's name, the copyright holder and the
 * "Powered by" line with its link. Empty fields keep each app's own branding.json. Stores receive it with their next sync;
 * each brand's logo is set on the brand.
 */
export function BrandingPage() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canManage = hasPermission(session, 'ho.org.branding.manage');
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: brandingKey,
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/identity/tenant-branding');
      if (!data) throw error;
      return data;
    },
  });
  const [edits, setEdits] = useState<Partial<Fields>>({});

  const saved = current.data;
  const value = (key: keyof Fields) => edits[key] ?? saved?.[key] ?? '';
  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.PUT('/api/v1/identity/tenant-branding', {
        body: {
          productName: value('productName'),
          copyright: value('copyright'),
          poweredByName: value('poweredByName'),
          poweredByUrl: value('poweredByUrl'),
          version: saved?.version ?? 0,
        },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setEdits({});
      queryClient.setQueryData(brandingKey, next);
    },
  });

  if (current.isLoading) return <SkeletonText paragraph lineCount={4} aria-label={t('common.loading')} />;
  if (!saved)
    return (
      <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(current.error))} />
    );

  const field = (key: keyof Fields, maxLength: number) => ({
    id: `branding-${key}`,
    labelText: t(`admin.branding.${key}`),
    helperText: t(`admin.branding.${key}Help`),
    value: value(key),
    maxLength,
    readOnly: !canManage,
    onChange: (e: { target: { value: string } }) => {
      save.reset();
      setEdits((all) => ({ ...all, [key]: e.target.value }));
    },
  });
  const holder = (value('copyright') || value('poweredByName') || value('productName')).trim();
  const changed = Object.keys(edits).length > 0;

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('admin.branding.title')} description={t('admin.branding.hint')} />
      <Stack gap={6}>
        <TextInput {...field('productName', 100)} />
        <TextInput {...field('copyright', 200)} />
        <TextInput {...field('poweredByName', 100)} />
        <TextInput {...field('poweredByUrl', 500)} type="url" />
        {/* What footers show, as the apps word it. */}
        <Tile aria-label={t('admin.branding.preview')}>
          <Stack gap={3}>
            <FormLabel>{t('admin.branding.preview')}</FormLabel>
            {value('poweredByName').trim() && <p>{t('common.poweredBy', { name: value('poweredByName').trim() })}</p>}
            {holder && <p>{t('common.copyright', { year: new Date().getFullYear(), holder })}</p>}
            {!holder && <p>{t('admin.branding.previewNone')}</p>}
          </Stack>
        </Tile>
        {canManage && (
          <div>
            <Button disabled={!changed || save.isPending} onClick={() => save.mutate()}>
              {t('admin.save')}
            </Button>
          </div>
        )}
        {save.error && (
          <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(save.error))} />
        )}
        {save.isSuccess && (
          <InlineNotification kind="success" role="status" lowContrast hideCloseButton title={t('admin.branding.saved')} />
        )}
      </Stack>
    </section>
  );
}
